import { useEffect, useState } from "react";
import { useNavigate, Outlet, NavLink, Link } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { fetchMe, logout } from "../../lib/api";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchMe();
        if (!me.is_admin) {
          navigate("/admin/login", {
            replace: true,
            state: { error: "Your account is not authorised." },
          });
          return;
        }
        if (!cancelled) {
          setUser(me);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          navigate("/admin/login", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onLogout = async () => {
    try {
      await logout();
    } catch (_) {
      /* ignore */
    }
    navigate("/admin/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-ink-muted font-accent">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-layout">
      <PageHeader
        overline={`Signed in as ${user?.email}`}
        title="Admin"
        lede="Manage messages and journal posts."
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-rule pb-6 mb-12">
          <nav className="flex gap-2 flex-wrap" data-testid="admin-nav">
            {[
              ["/admin", "Messages", true],
              ["/admin/journal", "Journal", false],
              ["/admin/stories", "Stories", false],
            ].map(([to, label, end]) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`admin-nav-${label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-accent font-semibold tracking-wide border transition-colors ${
                    isActive
                      ? "bg-ink text-linen border-ink"
                      : "border-rule text-ink-muted hover:border-ink hover:text-ink"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="text-sm font-accent text-ink-muted hover:text-ink underline underline-offset-4"
            >
              View site
            </Link>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="text-sm font-accent text-ink-muted hover:text-ink underline underline-offset-4"
            >
              Sign out
            </button>
          </div>
        </div>

        <Outlet context={{ user }} />
      </section>
    </div>
  );
}
